<template>
  <div v-if="pageVisible">
    <BackButton
      link="/agency/our-public-profiles/"
      text="Back to Our public profiles"
    />
    <dashboard-header
      heading="Leads"
      paragraph="View all of the businesses that have submitted a contact request"
    >
      <div v-if="leadFlowEnabled" class="flex " style="margin-top:10px">
        <div
          class="relative inline-block text-left mr-5"
          @mouseover="tooltipTotalLeadsOpen = true"
          @mouseleave="tooltipTotalLeadsOpen = false"
        >
          <div class="flex text-gray-gray hover:text-gray-dark">
            <p class="mr-2 fs-sm">{{ profile.totalLeads }}</p>

            <span>
              <svg
                class="h-5 w-5"
                style="margin:0px"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M8.707 7.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l2-2a1 1 0 00-1.414-1.414L11 7.586V3a1 1 0 10-2 0v4.586l-.293-.293z"
                />
                <path
                  d="M3 5a2 2 0 012-2h1a1 1 0 010 2H5v7h2l1 2h4l1-2h2V5h-1a1 1 0 110-2h1a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5z"
                />
              </svg>
            </span>

            <Tooltip
              v-if="tooltipTotalLeadsOpen"
              message="Number of leads you've received from your partner."
              css="width: 190px; z-index: 999999999; margin-left:-70px; margin-top:-55px;"
            />
          </div>
        </div>

        <div
          class="relative inline-block text-left mr-5"
          @mouseover="tooltipAcceptedLeadsOpen = true"
          @mouseleave="tooltipAcceptedLeadsOpen = false"
        >
          <div class="flex text-gray-gray hover:text-gray-dark">
            <p class="mr-2 fs-sm">{{ profile.acceptedLeads }}</p>
            <span>
              <svg
                class="h-5 w-5"
                style="margin:0px"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
            </span>
            <Tooltip
              v-if="tooltipAcceptedLeadsOpen"
              message="Number of leads you've accepted from your partner."
              css="width: 190px; z-index: 999999999; margin-left:-80px; margin-top:-70px;"
            />
          </div>
        </div>

        <div
          class="relative inline-block text-left"
          @mouseover="tooltipSecuredLeadsOpen = true"
          @mouseleave="tooltipSecuredLeadsOpen = false"
        >
          <div class="flex text-gray-gray hover:text-gray-dark">
            <p class="mr-2 fs-sm">{{ profile.securedLeads }}</p>

            <span>
              <svg
                class="h-5 w-5"
                style="margin:0px"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path
                  fill-rule="evenodd"
                  d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </span>

            <Tooltip
              v-if="tooltipSecuredLeadsOpen"
              message="Number of projects received from your partner that you've secured."
              css="width: 190px; z-index: 999999999; margin-left:-80px; margin-top:-70px;"
            />
          </div>
        </div>
      </div>
    </dashboard-header>

    <TableToolbar :query="filters.name.value" @update-query="filterRows">
      <div v-if="leadFlowEnabled">
        <label style="margin-bottom: 0">
          <div class="select select-mb-0">
            <select
              @change="
                filterRows(
                  ['project_status'],
                  $event.target.value,
                  'project_status'
                )
              "
            >
              <option selected="selected" value>Project status</option>
              <option :selected="filters.projectStatus.value === 'N'" value="N"
                >New lead</option
              >
              <option :selected="filters.projectStatus.value === 'C'" value="C"
                >Lead contacted</option
              >
              <option :selected="filters.projectStatus.value === 'L'" value="L"
                >Lead lost</option
              >
              <option :selected="filters.projectStatus.value === 'S'" value="S"
                >Project secured</option
              >
            </select>
          </div>
        </label>
      </div>
    </TableToolbar>
    <div class="mb-lg">
      <v-table
        :data="rows"
        :filters="filters"
        :currentPage.sync="currentPage"
        :pageSize="pageSize"
        class="table table-bordered"
        @totalPagesChanged="totalPages = $event"
        :totalItemsInput="count"
      >
        <thead slot="head">
          <v-th
            v-on:sortChange="sortChange"
            v-for="(column, i) in columns"
            :key="i"
            :sortKey="column.name"
            class="mb-xxs grey-background fs-sm"
            >{{ column.displayName }}</v-th
          >
        </thead>
        <tbody slot="body" slot-scope="{ displayData }">
          <tr
            v-for="(row, i) in displayData"
            :key="i"
            @click="navigate(row, $event)"
          >
            <td class="fs-sm f-capitalize fw-semibold">
              {{ row.companyName }}
            </td>
            <td class="fs-sm f-capitalize">{{ showDate(row.created) }}</td>
            <td class="fs-sm">
              <i class="fa fa-circle mr-xs" :class="row.status | circleClass" />
              {{ row.status | status }}
            </td>
            <td v-if="leadFlowEnabled" class="fs-sm f-capitalize ta-center">
              <span>{{ row.projectStatus | projectStatus }}</span>
            </td>
          </tr>
        </tbody>
      </v-table>
      <smart-pagination
        :currentPage.sync="currentPage"
        :count="count"
        :pageSize="pageSize"
        v-on:update:currentPage="pageChanged($event)"
      />
    </div>
  </div>
</template>

<script>
import { formatDate } from '@/utils/date';
import DashboardHeader from '@/components/Dashboard/DashboardHeader';
import TableToolbar from '@/components/Dashboard/TableToolbar';
import Tooltip from '@/components/DesignSystem/Tooltip';
import BackButton from '@/components/BackButton';

import { createNamespacedHelpers } from 'vuex';
const { mapGetters, mapActions, mapState } = createNamespacedHelpers(
  'ourPublicProfilesLeads'
);

export default {
  name: 'Leads',
  components: {
    TableToolbar,
    DashboardHeader,
    Tooltip,
    BackButton,
  },
  data() {
    return {
      pageVisible: false,
      currentPage: 1,
      totalPages: 3,
      tooltipTotalLeadsOpen: false,
      tooltipAcceptedLeadsOpen: false,
      tooltipSecuredLeadsOpen: false,
    };
  },
  filters: {
    status(value) {
      switch (value) {
        case 'P':
          return 'Pending';
        case 'A':
          return 'Accepted';
        case 'R':
          return 'Declined';
      }
    },
    projectStatus(value) {
      switch (value) {
        case 'N':
          return 'New lead';
        case 'C':
          return 'Lead contacted';
        case 'L':
          return 'Lead lost';
        case 'S':
          return 'Project secured';
        default:
          return '-';
      }
    },
    circleClass(value) {
      switch (value) {
        case 'P':
          return 'fc-light';
        case 'A':
          return 'fc-green';
        case 'R':
          return 'fc-alert';
      }
    },
  },
  computed: {
    ...mapGetters({
      rawColumns: 'getColumns',
      rows: 'getRows',
      filters: 'getFilters',
    }),
    ...mapState(['count', 'pageSize']),
    columns() {
      if (this.leadFlowEnabled) {
        const statusIndex = this.rawColumns.findIndex(col => {
          return col.name === 'status';
        });
        this.setDisplayName(statusIndex, 'Lead');
        return this.rawColumns;
      }
      return this.rawColumns.filter(col => {
        return col.name !== 'projectStatus';
      });
    },
    leadFlowEnabled() {
      return this.$store.getters['ourPublicProfilesDetail/profile']
        .leadFlowEnabled;
    },
    profile() {
      return this.$store.getters['ourPublicProfilesDetail/profile'];
    },
    loading() {
      return this.$store.getters['tabLoading'];
    },
  },
  async created() {
    await this.$store.dispatch(
      'ourPublicProfilesDetail/fetchProfile',
      this.$route.params.id
    );
    await this.fetchLeadsData(1);
    return (this.pageVisible = true);
  },
  methods: {
    ...mapActions([
      'updateQueryAndFilter',
      'fetchLeadsData',
      'setLeadDetail',
      'sortResults',
    ]),
    pageChanged(page) {
      this.fetchLeadsData(page);
    },
    sortChange(key) {
      this.sortResults(key);
      this.currentPage = 1;
    },
    filterRows(fields, value, type) {
      fields = fields || ['companyName'];
      this.updateQueryAndFilter({ fields, value, type });
    },
    showDate(date) {
      if (date) {
        return formatDate(new Date(String(date)));
      }
      return '-';
    },
    navigate(leadData, event) {
      this.setLeadDetail(leadData);
      this.$helpers.navigateOrNewTab(
        this.navigateURL(leadData),
        this.$router,
        event
      );
    },
    navigateURL(leadData) {
      this.setLeadDetail(leadData);
      return `/agency/our-public-profiles/leads/${this.$route.params.id}/lead/${leadData.id}`;
    },
    setDisplayName(index, name) {
      this.rawColumns[index].displayName = name;
    },
  },
};
</script>
